/**
 * The dialog window's half of the "use slide templates" wizard: owns the
 * outline, the template library, and the per-slide layout picks that step 4
 * shows.
 *
 * The window can do neither of the two loads itself — the outline call needs
 * a bearer token and the library SAS needs the pane's authorised HttpClient —
 * so both are asked of the pane over the Create-slides channel (see
 * service/createSlidesDialogChannel.ts). The library is then fetched HERE,
 * directly from Azure Blob with the SAS the pane replies with, exactly as the
 * Asset Library's slide-template picker does: a SAS query string is
 * self-contained auth, so the window needs nothing else.
 *
 * THE TWO LOADS ARE INDEPENDENT, AND ONLY ONE IS REQUIRED
 * The outline response already carries everything a row needs to render —
 * the brief, plus the chosen layout's own `name`/`layout_class`/`slide_index`.
 * The library only adds thumbnails and the swap gallery. So `choices` derives
 * from the OUTLINE ALONE and the library enriches it if and when it arrives:
 * a blocked library fetch degrades this step to "no previews, no swapping"
 * rather than hiding an outline that loaded perfectly.
 *
 * ONLY THE OUTLINE'S OWN LISTED TEMPLATES ARE LOADED BY DEFAULT
 * Both library manifests carry embeddings/vectors and both `.cache` files one
 * base64 image per entry — many MB, fetched on entering this step just to
 * decorate the handful of ids the outline already named. So entering the
 * step asks the pane for the outline and the libraries' directory SAS (one
 * cheap mint, no download), and then reads a wireframe for exactly the
 * listed ids out of each template's own `.pptx` by Range request — a few KB
 * per slide (service/templateSlidePreview.ts). The library itself downloads
 * only on an explicit "Browse the full library" (`requestLibrary`), which
 * mints a fresh SAS and then runs everything below.
 *
 * THE LIBRARY LOAD ITSELF IS TWO PHASES
 * fetchCatalog fetches each library's manifest (an id/category/geometry
 * index, no images) and flips catalogState to "ready" — wireframes, full swap
 * gallery, everything — the instant that arrives. Real photo thumbnails come
 * from a SEPARATE per-library `.cache` fetch, which can carry one base64
 * image per entry and so take far longer to download and parse on a large
 * catalog (see slideTemplatesApi.ts's own module header) — `startThumbnailFetch`
 * kicks it off itself the moment a catalog session begins (right after
 * `fetchCatalog` learns the libraries' SAS info), guarded so a session never
 * fetches them twice. Thumbnails are merged into the existing
 * `catalogState.templates` in place once they land — nothing about "ready" or swapping ever depended on it, so
 * both the row list and an already-open gallery upgrade to real photos
 * seamlessly whenever that turns out to be.
 *
 * BOTH THE MANIFEST AND THE `.cache` FETCH ARE STREAMED, AND THE OUTLINE'S OWN
 * SUGGESTIONS (every slide's `recommended_template` + `alternatives`) ARE
 * TREATED AS PRIORITY on both: as soon as one of those ids is parsed out of
 * either response — however far into the download it happens to sit — it's
 * applied immediately, rather than waiting for the rest of the (possibly
 * thousands-of-entries) file to finish. Because the `.cache` fetch now starts
 * before the outline is even guaranteed to have arrived (the SAS mint is
 * usually the FASTER of the two), priority matching reads live refs
 * (`priorityTemplateIdsRef` / `priorityThumbnailKeysRef`, both set on
 * "outline-ready") rather than a value captured when the fetch started — an
 * id becomes "wanted" the moment the outline names it, even mid-stream, not
 * only if it was already known when the download began. On the manifest side
 * this is what lets a suggestion show a wireframe (`priorityTemplates`,
 * merged into `templatesById` alongside — never instead of — the full
 * `catalogState` once THAT'S ready) before the whole catalog has loaded,
 * which is what the gallery's default suggestions-only view (see
 * StepPickLayouts) needs to avoid a blank card. This is best-effort, not a
 * guarantee: the backend controls each file's ordering, not the frontend, so
 * a priority id near the end of a stream still waits until it's reached. The
 * eventual full merge at the end of each fetch is unchanged and remains
 * authoritative.
 *
 * WHICH LAYOUT A SLIDE GETS
 * The ranker's `recommended_template`, then its alternatives in order — the
 * library is deliberately NOT consulted. Gating on presence would reject a
 * pick the backend is perfectly happy with, and would make this step behave
 * differently depending on whether the library happened to load. A template
 * that turns out not to be fetchable is reported per slide at assembly time
 * instead — see service/templateGenerator.ts. `unresolvedCount` therefore
 * counts only slides the ranker offered NOTHING for, which is the backend's
 * own `covered=false` state.
 *
 * EDITING THE OUTLINE
 * The outline the backend returns is a starting point, not the final word:
 * editSlide/addSlide/removeSlide/moveSlide let the user rewrite a brief,
 * insert or drop a slide, and reorder the deck before anything is built.
 * That state lives in OutlineEditorState (order + local additions + content
 * edits) layered on top of the outline's own slides — never mutating it —
 * so "Regenerate" can always throw the layer away and start over. A fresh
 * outline (whether the first one or a regenerated one) resets the layer,
 * since edits/additions/order belong to the briefs that produced them.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { logEvent } from "../../../../../telemetry";
import {
  applyThumbnailLookup,
  fetchSlideTemplateManifestStreaming,
  fetchSlideTemplateThumbnailsStreaming,
  logSlideTemplatesError,
  thumbnailKeyFromCatalogId,
  type SlideTemplate,
  type SlideTemplatesSasInfo,
} from "../../slide-templates/services/slideTemplatesApi";
import { getErrorMessage, isAbortError } from "../../shared/services/connectorErrors";
import {
  parseCreateSlidesPaneMessage,
  type TemplateLibrarySas,
} from "../service/createSlidesDialogChannel";
import { canReceiveFromParent, postToPane } from "../service/createSlidesDialogMessaging";
import type { OutlineResponse, PlannedSlide, TemplateMatch } from "../service/templatesApi";
import { fetchTemplateSlidePreviews } from "../service/templateSlidePreview";
import { SLIDE_COUNT_MAX, type PostedCreateSlideConfig, type TemplatePlan, type TemplatePlanSlide } from "./types";

const DECKORATE_CONNECTOR = "deckorate";

/** Defensive ceilings on manual outline edits — the backend imposes none of
 *  its own on these fields, so the UI holds the line against a pathological
 *  paste (a title/bullet dumped in from a full document, say). */
const MAX_TITLE_LENGTH = 120;
const MAX_BULLET_LENGTH = 200;
const MAX_MAIN_POINTS = 8;

const NO_CHANNEL_MESSAGE =
  "This window can't reach the task pane, so slide templates aren't available. Go back and choose AI design instead.";
const CATALOG_FETCH_FAILED_MESSAGE = "Couldn't load the slide template library.";

/** Hostname of a SAS URL, for an error message that names what was unreachable. */
function sasHost(sasUrl: string): string {
  try {
    return new URL(sasUrl).hostname;
  } catch {
    return "blob storage";
  }
}

/**
 * Azure's error XML → "AuthorizationFailure: This request is not authorized…".
 *
 * A failed blob GET's body is an XML `<Error><Code/><Message/></Error>`, and
 * its `Message` carries a RequestId + timestamp appended after a newline.
 * Dumping that raw into the UI is unreadable, but the `Code` is the single
 * most useful thing on screen (it's what an Azure diagnosis starts from), so
 * it's kept and the noise dropped.
 */
function azureErrorSummary(body: string): string | null {
  const code = /<Code>([^<]+)<\/Code>/.exec(body)?.[1]?.trim();
  if (!code) return null;
  const message = /<Message>([^<]+)<\/Message>/.exec(body)?.[1]?.split(/\r?\n/)[0]?.trim();
  return message ? `${code}: ${message}` : code;
}

/** ".../standard-layouts/standard-layouts.signatures.json" -> "standard-layouts",
 *  so a failure can name WHICH library it was. Deliberately a local 3-liner
 *  rather than an import: `folderOfManifestPath` lives in the pane-only
 *  service/templateCatalogApi.ts, and this hook runs in the dialog. */
function libraryLabel(manifestBlobPath: string): string {
  const segments = manifestBlobPath.split("/").filter(Boolean);
  return segments.length >= 2 ? segments[segments.length - 2]! : manifestBlobPath;
}

/**
 * What went wrong with ONE library, in as few words as carry information.
 *
 * Two failure shapes, and they mean very different things:
 *
 *   - A bare TypeError ("Failed to fetch" / "NetworkError..." / "Load failed",
 *     depending on the browser) means the request never completed - no
 *     status, no body, nothing to inspect. For a direct-to-Azure GET carrying
 *     `x-ms-version` (which `sasVersionHeaders` adds, making the request
 *     non-simple and so preflighted) the usual cause is the storage account's
 *     CORS rules - but a proxy or DNS block looks identical from here, so
 *     this no longer asserts CORS as THE cause.
 *   - Anything else means Azure (or the parser) actually answered, and that
 *     answer is the useful part - its XML unwrapped rather than pasted in.
 */
function libraryFailureSummary(error: unknown): string {
  if (error instanceof TypeError) return "request blocked before it completed";
  const raw = getErrorMessage(error, CATALOG_FETCH_FAILED_MESSAGE);
  const summary = azureErrorSummary(raw);
  if (!summary) return raw;
  const status = /\((\d{3})\s/.exec(raw)?.[1];
  return status ? `${status} ${summary}` : summary;
}

/**
 * The notice shown when NO library could be read: one line per library, so a
 * screenshot of it is enough to tell a CORS block from a 403 from a missing
 * ClientAssets row - and which folder each applies to.
 */
function catalogErrorMessage(
  failures: ReadonlyArray<{ readonly label: string; readonly error: unknown }>,
  host: string,
): string {
  if (failures.length === 0) return CATALOG_FETCH_FAILED_MESSAGE;
  const detail = failures
    .map((failure) => `${failure.label}: ${libraryFailureSummary(failure.error)}`)
    .join(" \u00b7 ");
  const blocked = failures.every((failure) => failure.error instanceof TypeError);
  const hint = blocked
    ? " Nothing reached Azure, so check the storage account's CORS rules (x-ms-version on GET) and any network/proxy block."
    : "";
  return `Couldn't read the slide template library at ${host} \u2014 ${detail}.${hint}`;
}

export type OutlineLoadState =
  | { readonly status: "idle" }
  | { readonly status: "loading" }
  | { readonly status: "error"; readonly message: string }
  | { readonly status: "ready"; readonly outline: OutlineResponse };

export type CatalogLoadState =
  | { readonly status: "idle" }
  | { readonly status: "loading" }
  | { readonly status: "error"; readonly message: string }
  | { readonly status: "ready"; readonly templates: readonly SlideTemplate[] };

/** One row of the step-4 list: the brief, its current layout, and whether
 *  that layout came from the ranker or from the user. */
export interface SlideLayoutChoice {
  readonly slide: PlannedSlide;
  /** null → the ranker offered no layout at all; the user MUST pick one. */
  readonly templateId: string | null;
  readonly autoPicked: boolean;
  /**
   * Display name for `templateId`, always populated when one is set: the
   * library entry's name if the library loaded, else the ranker's own `name`
   * from the outline, else the id's filename. This is what keeps a row
   * meaningful with no library.
   */
  readonly layoutName: string;
  /** The library entry behind `templateId` — thumbnail/preview only, and null
   *  until (or unless) the library loads. */
  readonly template: SlideTemplate | null;
  /** Every candidate the ranker offered for this slide, best first. */
  readonly suggestions: readonly TemplateMatch[];
}

/** Every candidate the ranker offered for a slide, best first, de-duped. */
function suggestionsFor(slide: PlannedSlide): TemplateMatch[] {
  const seen = new Set<string>();
  const suggestions: TemplateMatch[] = [];
  for (const candidate of [slide.recommended_template, ...(slide.alternatives ?? [])]) {
    if (!candidate?.id || seen.has(candidate.id)) continue;
    seen.add(candidate.id);
    suggestions.push(candidate);
  }
  return suggestions;
}

/** "transform-layouts/Cover_Slide.pptx#2" → "Cover Slide". The last-resort
 *  label when neither the library nor the ranker named the layout. Exported
 *  for StepPickLayouts' swap-gallery quick-pick cards, which need the exact
 *  same fallback before the full catalog (and its real names) has loaded. */
export function nameFromTemplateId(templateId: string): string {
  const file = templateId.split("/").pop() ?? templateId;
  return (
    file
      .replace(/#\d+\s*$/, "")
      .replace(/\.pptx$/i, "")
      .replace(/[_-]+/g, " ")
      .trim() || templateId
  );
}

/** The editable fields of a slide's brief — everything step 4 lets the user
 *  rewrite by hand, distinct from the layout pick which `choose` owns. */
export interface SlideContent {
  readonly title: string;
  readonly subtitle: string | null;
  readonly main_points: readonly string[];
}

/** Outline-editing state: which slides are in the deck, in what order, which
 *  ones the user added locally, and the content edits applied on top of the
 *  outline's own briefs. Held as one object so every edit (add/remove/move/
 *  rewrite) updates atomically — see addSlide/removeSlide/moveSlide/editSlide. */
interface OutlineEditorState {
  /** slide_idx values in deck order — the single source of truth for both
   *  membership (a removed slide is simply absent) and position. */
  readonly order: readonly number[];
  /** Slides the user added on the client, keyed by a synthetic negative
   *  slide_idx (the outline only ever hands out >= 0), since they have no
   *  outline brief of their own to fall back on. */
  readonly addedSlides: ReadonlyMap<number, PlannedSlide>;
  /** Rewrites of an outline (or added) slide's title/subtitle/bullets. */
  readonly edits: ReadonlyMap<number, SlideContent>;
  /** Next id `addSlide` will hand out (counts down from -1). */
  readonly nextLocalIdx: number;
}

const EMPTY_EDITOR_STATE: OutlineEditorState = {
  order: [],
  addedSlides: new Map(),
  edits: new Map(),
  nextLocalIdx: -1,
};

/** Where a new slide lands in `order`: right after `after`, or at the end
 *  when `after` is null or no longer present (e.g. it was just removed). */
function insertionIndex(order: readonly number[], after: number | null): number {
  if (after === null) return order.length;
  const at = order.indexOf(after);
  return at === -1 ? order.length : at + 1;
}

/** A freshly added slide: content is entirely user-authored, so it starts
 *  blank and with no template match — `covered: false` forces the same
 *  "choose a layout" gate an uncovered outline slide gets. */
function blankSlide(slideIdx: number): PlannedSlide {
  return {
    slide_idx: slideIdx,
    title: "New slide",
    subtitle: null,
    intent_tag: "body",
    main_points: [],
    recommended_template: null,
    alternatives: [],
    covered: false,
  };
}

/** Trims and bounds one edit so a pasted wall of text can't blow up a row or
 *  the deck it becomes — empty bullets are dropped rather than kept as blanks. */
function sanitizeContent(content: SlideContent): SlideContent | null {
  const title = content.title.trim().slice(0, MAX_TITLE_LENGTH);
  if (!title) return null;
  const subtitle = content.subtitle?.trim().slice(0, MAX_TITLE_LENGTH) || null;
  const main_points = content.main_points
    .map((point) => point.trim().slice(0, MAX_BULLET_LENGTH))
    .filter(Boolean)
    .slice(0, MAX_MAIN_POINTS);
  return { title, subtitle, main_points };
}

export interface TemplatePicker {
  readonly outlineState: OutlineLoadState;
  readonly catalogState: CatalogLoadState;
  /** Every template this session currently knows about, by id — the full
   *  catalog once `catalogState` is "ready", plus (before or regardless of
   *  that) any outline-suggested id the manifest stream has already found.
   *  What the swap gallery's default suggestions-only view enriches its
   *  cards from — see hooks/useTemplatePicker.ts's module header. */
  readonly templatesById: ReadonlyMap<string, SlideTemplate>;
  /** One row per slide, in deck order. Empty until both loads are ready. */
  readonly choices: readonly SlideLayoutChoice[];
  /** Slides with no layout yet — "Next" stays disabled while this is > 0. */
  readonly unresolvedCount: number;
  /** Asks the pane for the outline (idempotent per config change). */
  readonly loadOutline: (config: PostedCreateSlideConfig) => void;
  /** Re-runs the outline from scratch — the step's "Regenerate" action. */
  readonly regenerate: () => void;
  /** Re-attempts the full library after it failed — same as `requestLibrary`. */
  readonly retryCatalog: () => void;
  /** Downloads the FULL library (manifests, then real photos). Large and slow,
   *  so only on an explicit "Browse the full library" — never automatically.
   *  Idempotent while a load is in flight or done. */
  readonly requestLibrary: () => void;
  readonly choose: (slideIdx: number, templateId: string) => void;
  /** Drops every manual override back to the ranker's pick. */
  readonly autoSelectAll: () => void;
  /** Rewrites a slide's title/subtitle/bullets. A blank title is rejected
   *  (the edit is dropped) rather than left to produce an unreadable slide. */
  readonly editSlide: (slideIdx: number, content: SlideContent) => void;
  /** Inserts a blank, user-authored slide after `afterSlideIdx` (end of deck
   *  when null). No-op once the deck is already at SLIDE_COUNT_MAX. */
  readonly addSlide: (afterSlideIdx: number | null) => void;
  /** Drops a slide from the deck. No-op on the last remaining slide — a deck
   *  needs at least one. */
  readonly removeSlide: (slideIdx: number) => void;
  /** Swaps a slide with its neighbour; a no-op at either end of the deck. */
  readonly moveSlide: (slideIdx: number, direction: "up" | "down") => void;
  /** Whether the deck can take another slide (below SLIDE_COUNT_MAX). */
  readonly canAddSlide: boolean;
  /** The approved plan, or null while anything is still missing. */
  readonly plan: TemplatePlan | null;
}

export function useTemplatePicker(): TemplatePicker {
  const [outlineState, setOutlineState] = useState<OutlineLoadState>({ status: "idle" });
  const [catalogState, setCatalogState] = useState<CatalogLoadState>({ status: "idle" });
  /** slide_idx -> the user's explicit choice. Absent = use the auto-pick. */
  const [overrides, setOverrides] = useState<ReadonlyMap<number, string>>(new Map());
  /** Order, additions and content rewrites layered on top of the outline —
   *  see OutlineEditorState. */
  const [editor, setEditor] = useState<OutlineEditorState>(EMPTY_EDITOR_STATE);
  /** Manifest entries for outline-suggested ids, filled in as the manifest
   *  streams — BEFORE catalogState necessarily reaches "ready". See
   *  fetchCatalog / templatesById below and the module header. */
  const [priorityTemplates, setPriorityTemplates] = useState<ReadonlyMap<string, SlideTemplate>>(new Map());
  /** The libraries' directory SAS, once the pane has minted it. */
  const [librarySas, setLibrarySas] = useState<readonly TemplateLibrarySas[] | null>(null);
  /** Wireframes for the outline's own listed ids, read by Range request —
   *  see the effect next to `templatesById`. */
  const [listedTemplates, setListedTemplates] = useState<ReadonlyMap<string, SlideTemplate>>(new Map());
  /** Ids `listedTemplates` already holds, so a later outline re-reads only new ones. */
  const previewedIdsRef = useRef<Set<string>>(new Set());
  /** Set by `requestLibrary` — only then does a SAS reply start the full download. */
  const libraryWantedRef = useRef(false);
  /** The first `loadOutline` asks for the SAS; later ones reuse it. */
  const sasRequestedRef = useRef(false);

  const isMountedRef = useRef(true);
  /** The config the outline was last requested for, so a re-entered step 4
   *  doesn't refetch an outline it already has. */
  const requestedConfigRef = useRef<string | null>(null);
  const catalogAbortRef = useRef<AbortController | null>(null);
  /** Guards the (large, potentially slow) thumbnail fetch to once per catalog
   *  session — reset whenever a fresh `fetchCatalog` starts — so clicking
   *  "Change" on a second slide doesn't re-download the same `.cache` files. */
  const thumbnailsRequestedRef = useRef(false);
  /** The CURRENT outline's own suggested ids (every slide's
   *  `recommended_template` + `alternatives`) — a ref, not state, because the
   *  manifest stream reads it from inside a callback that must always see the
   *  latest outline, not whatever was current when fetchCatalog started (the
   *  two loads race; the outline can arrive after the manifest stream is
   *  already under way). Set on "outline-ready"; entries streamed in before
   *  that just don't get prioritised — see the module header's caveat. */
  const priorityTemplateIdsRef = useRef<ReadonlySet<string>>(new Set());
  /** The same outline suggestions, in the `.cache` file's OWN key format
   *  (`{basename#slideIndex}`, via `thumbnailKeyFromCatalogId`) — a separate
   *  ref because the two streams key their entries differently. Read live for
   *  the same reason as `priorityTemplateIdsRef` above: the `.cache` fetch now
   *  starts as soon as the catalog session does (see `startThumbnailFetch`),
   *  routinely before the outline has even arrived. */
  const priorityThumbnailKeysRef = useRef<ReadonlySet<string>>(new Set());
  /** Real-photo priority hits whose matching manifest entry hasn't streamed
   *  in yet (`priorityTemplates` doesn't have it), keyed the same way —
   *  applied retroactively the moment that entry does arrive (see
   *  `onManifestEntry` in fetchCatalog). The `.cache` and manifest fetches
   *  race each other too, same as the outline vs. either of them. */
  const pendingPriorityThumbnailsRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      catalogAbortRef.current?.abort();
    };
  }, []);

  /**
   * PHASE 2 — real photo thumbnails, layered onto an already-"ready" catalog
   * once they arrive. Deliberately fire-and-forget from the caller's point of
   * view: the step is already fully usable off the manifest alone (phase 1),
   * and a library's `.cache` file can carry one base64 image per entry —
   * large enough on a real catalog (thousands of entries) to take minutes to
   * download and parse (see slideTemplatesApi.ts's module header). Gating
   * "ready"/swappable on that would trade a fast, fully-functional step for a
   * slow one just to have photos instead of wireframes a little sooner.
   *
   * Never touches catalogState's status or reports an error of its own — a
   * slow or failed `.cache` fetch just leaves cards on their wireframe
   * preview, exactly as if the library published no photos at all.
   *
   * Priority is read live from `priorityThumbnailKeysRef` on every entry, not
   * a value captured when this started — see the module header for why. A
   * match is applied to whichever representation currently holds that
   * template: `priorityTemplates` (before catalogState is "ready") and/or the
   * ready catalog itself, and stashed in `pendingPriorityThumbnailsRef` too in
   * case the matching manifest entry hasn't streamed in yet either.
   */
  const fetchThumbnails = useCallback(
    (sasInfos: readonly SlideTemplatesSasInfo[], signal: AbortSignal) => {
      const startedAt = Date.now();
      console.info(
        `[create-slide] fetching real thumbnails for ${sasInfos.length} librar${sasInfos.length === 1 ? "y" : "ies"} (${priorityThumbnailKeysRef.current.size} prioritised so far)…`,
      );
      const applyPriorityMatch = (key: string, url: string) => {
        if (signal.aborted || !isMountedRef.current || !priorityThumbnailKeysRef.current.has(key)) return;
        pendingPriorityThumbnailsRef.current.set(key, url); // retroactive, if the manifest entry lands later
        const lookup = new Map([[key, url]]);
        setPriorityTemplates((prev) => {
          if (prev.size === 0) return prev;
          return new Map(applyThumbnailLookup([...prev.values()], lookup).map((t) => [t.id, t]));
        });
        setCatalogState((prev) =>
          prev.status === "ready" ? { status: "ready", templates: applyThumbnailLookup(prev.templates, lookup) } : prev,
        );
      };
      // Fire-and-forget: allSettled never rejects, and every outcome is handled below.
      void Promise.allSettled(
        sasInfos.map((sasInfo) => fetchSlideTemplateThumbnailsStreaming(sasInfo, signal, applyPriorityMatch)),
      ).then((results) => {
        if (signal.aborted) {
          console.info(`[create-slide] thumbnail fetch aborted after ${Date.now() - startedAt}ms (superseded)`);
          return;
        }
        if (!isMountedRef.current) return;
        const merged = new Map<string, string>();
        for (const result of results) {
          if (result.status !== "fulfilled") continue;
          for (const [key, url] of result.value) merged.set(key, url);
        }
        console.info(
          `[create-slide] thumbnail fetch settled after ${Date.now() - startedAt}ms: ${merged.size} photo(s) across ${results.length} librar${results.length === 1 ? "y" : "ies"}`,
        );
        if (merged.size === 0) return; // nothing to enrich with — cards keep their wireframes
        setCatalogState((prev) =>
          prev.status === "ready" ? { status: "ready", templates: applyThumbnailLookup(prev.templates, merged) } : prev,
        );
      });
    },
    [],
  );

  /**
   * Starts PHASE 2 (real photo thumbnails) for the given session — called by
   * fetchCatalog the instant a session begins. Guarded so the same session's
   * `.cache` files are never fetched twice.
   */
  const startThumbnailFetch = useCallback(
    (sasInfos: readonly SlideTemplatesSasInfo[], controller: AbortController) => {
      if (thumbnailsRequestedRef.current || sasInfos.length === 0) return;
      thumbnailsRequestedRef.current = true;
      fetchThumbnails(sasInfos, controller.signal);
    },
    [fetchThumbnails],
  );

  const fetchCatalog = useCallback(
    (libraries: readonly TemplateLibrarySas[]) => {
      // Abort a still-in-flight fetch from an earlier SAS reply — its result
      // would otherwise race this newer one and could win.
      catalogAbortRef.current?.abort();
      const controller = new AbortController();
      catalogAbortRef.current = controller;
      setCatalogState({ status: "loading" });
      thumbnailsRequestedRef.current = false; // fresh session — nobody's fetched photos for it yet
      setPriorityTemplates(new Map()); // fresh session — any earlier priority hits belonged to it
      pendingPriorityThumbnailsRef.current = new Map();

      const sasInfos = libraries.map(
        (library): SlideTemplatesSasInfo => ({ directorySasUrl: library.sasUrl, manifestBlobPath: library.manifestBlobPath }),
      );
      // Real photos start now too — see the module header for why this no
      // longer waits for the user to click a row's "Change".
      startThumbnailFetch(sasInfos, controller);

      // Any manifest entry matching an outline-suggested id is applied to
      // `priorityTemplates` the instant it's parsed — see the module header —
      // regardless of how much of this (or any other) library's manifest has
      // streamed in yet. `priorityTemplateIdsRef` (not outlineState) because
      // this callback must see whichever outline is CURRENT when an entry
      // arrives, not whatever was current when fetchCatalog started.
      const onManifestEntry = (template: SlideTemplate) => {
        if (controller.signal.aborted || !isMountedRef.current) return;
        if (!priorityTemplateIdsRef.current.has(template.id)) return;
        setPriorityTemplates((prev) => {
          if (prev.has(template.id)) return prev;
          // A real photo for this exact id may have already streamed in from
          // the (independently racing) `.cache` fetch — apply it now rather
          // than starting this card off on its wireframe and never upgrading.
          const enriched =
            pendingPriorityThumbnailsRef.current.size > 0
              ? applyThumbnailLookup([template], pendingPriorityThumbnailsRef.current)[0]!
              : template;
          return new Map(prev).set(template.id, enriched);
        });
      };

      // PHASE 1 — the manifest alone. It's an id/category/geometry index with
      // no images in it at all, and it's everything a card needs to render as
      // a wireframe and be swapped. Each library is fetched independently so
      // one unavailable folder still leaves the other's layouts previewable
      // and swappable.
      Promise.allSettled(
        sasInfos.map((sasInfo) => fetchSlideTemplateManifestStreaming(sasInfo, controller.signal, onManifestEntry)),
      )
        .then((results) => {
          if (controller.signal.aborted || !isMountedRef.current) return;
          const templates = results.flatMap((result) =>
            result.status === "fulfilled" ? result.value : [],
          );
          const failures = results.flatMap((result, index) =>
            result.status === "rejected"
              ? [{ label: libraryLabel(libraries[index]!.manifestBlobPath), error: result.reason }]
              : [],
          );

          if (templates.length > 0) {
            setCatalogState({ status: "ready", templates });
            for (const failure of failures) {
              // Partial success: previews still work, just not for that folder.
              console.warn(`[create-slide] template library ${failure.label} unavailable:`, failure.error);
            }
            return;
          }
          // An abort isn't a failure to report - a newer fetch superseded this.
          if (failures.some((failure) => isAbortError(failure.error))) return;

          const message = catalogErrorMessage(failures, sasHost(libraries[0]?.sasUrl ?? ""));
          for (const failure of failures) {
            // The raw error too: `message` is the rewritten, user-facing one,
            // and a network-level failure carries nothing else to trace it by.
            logSlideTemplatesError(
              "create-slides-dialog-catalog-fetch",
              `${failure.label}: ${getErrorMessage(failure.error, CATALOG_FETCH_FAILED_MESSAGE)}`,
            );
            console.error(`[create-slide] template library ${failure.label} failed:`, failure.error);
          }
          setCatalogState({ status: "error", message });
        })
        .catch((error: unknown) => {
          if (isAbortError(error) || controller.signal.aborted || !isMountedRef.current) return;
          setCatalogState({
            status: "error",
            message: catalogErrorMessage([{ label: "template library", error }], ""),
          });
        });
    },
    [startThumbnailFetch],
  );

  /** Asks the pane to mint the libraries' directory SAS — one cheap API call,
   *  no downloads. What happens on the reply depends on `libraryWantedRef`
   *  (see the "catalog-ready" handler). False when there's no channel. */
  const requestSas = useCallback(
    () => postToPane({ type: "catalog-request" }, "create-slides-dialog-catalog-requested"),
    [],
  );

  /**
   * Downloads the FULL library (manifests, then `.cache` photos) — only ever
   * on an explicit "Browse the full library" (or a Retry after that failed).
   * Always mints a fresh SAS: the one minted for the listed previews may be
   * minutes old by now.
   */
  const requestLibrary = useCallback(() => {
    libraryWantedRef.current = true;
    if (catalogState.status === "loading" || catalogState.status === "ready") return;
    setCatalogState({ status: "loading" });
    if (!requestSas()) setCatalogState({ status: "error", message: NO_CHANNEL_MESSAGE });
  }, [catalogState.status, requestSas]);

  // ONE inbound handler for every pane -> dialog message. Office offers no
  // removeHandler for DialogParentMessageReceived and may replace rather than
  // add on a second registration, so this must be the only registration in
  // the window and must be in place before anything is requested.
  useEffect(() => {
    if (!canReceiveFromParent()) {
      logEvent("connector.action.invoke", "warn", {
        connectorSystemName: DECKORATE_CONNECTOR,
        reason: "create-slides-dialog-parent-messages-unsupported",
      });
      if (isMountedRef.current) {
        setOutlineState({ status: "error", message: NO_CHANNEL_MESSAGE });
        setCatalogState({ status: "error", message: NO_CHANNEL_MESSAGE });
      }
      return;
    }

    const handler = (arg: unknown) => {
      const message = parseCreateSlidesPaneMessage(arg);
      if (!message) {
        logEvent("connector.action.invoke", "warn", {
          connectorSystemName: DECKORATE_CONNECTOR,
          reason: "create-slides-dialog-parent-message-unrecognised",
        });
        return;
      }
      if (!isMountedRef.current) return;
      switch (message.type) {
        case "outline-ready":
          setOutlineState({ status: "ready", outline: message.outline });
          // A fresh outline replaces whatever order/additions/edits were
          // layered on the previous one — those belong to briefs that no
          // longer exist.
          setEditor({
            ...EMPTY_EDITOR_STATE,
            order: message.outline.slides.map((slide) => slide.slide_idx),
          });
          // This outline's own suggested ids become both streams' priority
          // sets (see fetchCatalog / startThumbnailFetch) — a fresh outline
          // means a fresh set, and whatever priority hits belonged to the old
          // one are gone with it. Two ref formats because the manifest and
          // the `.cache` file key their entries differently — see
          // priorityThumbnailKeysRef's own doc.
          {
            const suggestedIds = message.outline.slides.flatMap((slide) => suggestionsFor(slide).map((s) => s.id));
            priorityTemplateIdsRef.current = new Set(suggestedIds);
            priorityThumbnailKeysRef.current = new Set(
              suggestedIds.flatMap((id) => {
                const key = thumbnailKeyFromCatalogId(id);
                return key ? [key] : [];
              }),
            );
          }
          setPriorityTemplates(new Map());
          pendingPriorityThumbnailsRef.current = new Map();
          return;
        case "outline-error":
          setOutlineState({ status: "error", message: message.message });
          return;
        case "catalog-ready":
          // Always: the listed-template previews read through these tokens.
          setLibrarySas(message.libraries);
          // Only on an explicit browse: the full download.
          if (libraryWantedRef.current) fetchCatalog(message.libraries);
          return;
        case "catalog-error":
          // Without a browse request this only costs the listed previews —
          // rows keep their names, so there's nothing to surface.
          if (libraryWantedRef.current) setCatalogState({ status: "error", message: message.message });
          else console.warn("[create-slide] template SAS unavailable; no layout previews:", message.message);
          return;
      }
    };

    try {
      Office.context.ui.addHandlerAsync(Office.EventType.DialogParentMessageReceived, handler, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) return;
        logEvent("connector.action.invoke", "warn", {
          connectorSystemName: DECKORATE_CONNECTOR,
          reason: "create-slides-dialog-parent-handler-failed",
        });
        if (isMountedRef.current) {
          setOutlineState({ status: "error", message: NO_CHANNEL_MESSAGE });
          setCatalogState({ status: "error", message: NO_CHANNEL_MESSAGE });
        }
      });
    } catch {
      logEvent("connector.action.invoke", "warn", {
        connectorSystemName: DECKORATE_CONNECTOR,
        reason: "create-slides-dialog-parent-handler-threw",
      });
      if (isMountedRef.current) {
        setOutlineState({ status: "error", message: NO_CHANNEL_MESSAGE });
        setCatalogState({ status: "error", message: NO_CHANNEL_MESSAGE });
      }
    }
    // Office has no removeHandler for DialogParentMessageReceived — the window
    // being destroyed is the teardown. isMountedRef guards late callbacks.
  }, [fetchCatalog]);

  const requestOutline = useCallback((config: PostedCreateSlideConfig) => {
    setOutlineState({ status: "loading" });
    setOverrides(new Map());
    setEditor(EMPTY_EDITOR_STATE);
    if (!postToPane({ type: "outline-request", config }, "create-slides-dialog-outline-requested")) {
      setOutlineState({ status: "error", message: NO_CHANNEL_MESSAGE });
    }
  }, []);

  const loadOutline = useCallback(
    (config: PostedCreateSlideConfig) => {
      // The outline, plus the SAS the listed-template previews read through —
      // never the library itself (see the module header).
      const key = JSON.stringify(config);
      if (requestedConfigRef.current === key) return;
      requestedConfigRef.current = key;
      requestOutline(config);
      if (!sasRequestedRef.current) {
        sasRequestedRef.current = true;
        requestSas();
      }
    },
    [requestOutline, requestSas],
  );

  /** The config the current outline belongs to, replayed by "Regenerate". */
  const lastConfigRef = useRef<PostedCreateSlideConfig | null>(null);
  const loadOutlineTracked = useCallback(
    (config: PostedCreateSlideConfig) => {
      lastConfigRef.current = config;
      loadOutline(config);
    },
    [loadOutline],
  );

  const regenerate = useCallback(() => {
    const config = lastConfigRef.current;
    if (!config) return;
    // Deliberately bypasses `loadOutline`'s de-dupe — the whole point of
    // "Regenerate" is a fresh outline for the SAME input, which the de-dupe
    // would otherwise swallow. The key is left in place so re-entering the
    // step afterwards still doesn't refetch.
    requestOutline(config);
  }, [requestOutline]);

  // Priority hits first (all that's known pre-"ready"), then the full catalog
  // once it lands — which wins on a shared id, since it's the more complete,
  // possibly-already-photo-enriched version. See the module header and
  // StepPickLayouts' default suggestions-only gallery view, which is what
  // actually needs a suggestion's template BEFORE the whole catalog is ready.
  // The listed-template wireframes (see the effect below) sit underneath both:
  // they're what every row and suggestion card shows when nobody browses.
  const templatesById = useMemo(() => {
    const map = new Map<string, SlideTemplate>(listedTemplates);
    for (const [id, template] of priorityTemplates) map.set(id, template);
    if (catalogState.status === "ready") {
      for (const template of catalogState.templates) map.set(template.id, template);
    }
    return map;
  }, [catalogState, priorityTemplates, listedTemplates]);

  /**
   * Wireframes for exactly the ids the outline listed, read out of each
   * template's own `.pptx` by Range request (service/templateSlidePreview.ts)
   * — no library download. Each slide's recommended pick goes first, so rows
   * fill in before the alternatives behind "Change" do. Ids already previewed
   * are skipped, so a regenerated outline only reads what's new.
   */
  useEffect(() => {
    if (outlineState.status !== "ready" || !librarySas) return;
    const slides = outlineState.outline.slides;
    const ordered = [
      ...slides.flatMap((slide) => (slide.recommended_template?.id ? [slide.recommended_template] : [])),
      ...slides.flatMap((slide) => slide.alternatives ?? []),
    ];
    const matchById = new Map<string, TemplateMatch>();
    for (const match of ordered) if (match.id && !matchById.has(match.id)) matchById.set(match.id, match);
    const wanted = [...matchById.keys()].filter((id) => !previewedIdsRef.current.has(id));
    if (wanted.length === 0) return;

    const controller = new AbortController();
    void fetchTemplateSlidePreviews(wanted, librarySas, controller.signal, (id, preview) => {
      if (!isMountedRef.current) return;
      previewedIdsRef.current.add(id);
      const match = matchById.get(id);
      const parsedIndex = /#(\d+)\s*$/.exec(id)?.[1];
      setListedTemplates((prev) =>
        new Map(prev).set(id, {
          id,
          name: match?.name ?? nameFromTemplateId(id),
          category: null,
          layoutClass: match?.layout_class ?? null,
          description: null,
          thumbnailUrl: null,
          preview,
          // Display-only: the file is resolved independently at generate time.
          insertable: false,
          sourceFileUrl: null,
          slideIndex: match?.slide_index ?? (parsedIndex ? Number(parsedIndex) : null),
        }),
      );
    });
    return () => controller.abort();
  }, [outlineState, librarySas]);

  /** The outline's own briefs, keyed by slide_idx, before any local edits. */
  const baseSlidesById = useMemo(() => {
    if (outlineState.status !== "ready") return new Map<number, PlannedSlide>();
    return new Map(outlineState.outline.slides.map((slide) => [slide.slide_idx, slide]));
  }, [outlineState]);

  /** The outline's slides plus the user's additions, in the user's order,
   *  each with any content edit applied on top. This — not the raw outline —
   *  is what `choices` and the plan are built from. */
  const orderedSlides = useMemo<PlannedSlide[]>(() => {
    return editor.order.flatMap((slideIdx) => {
      const base = baseSlidesById.get(slideIdx) ?? editor.addedSlides.get(slideIdx);
      if (!base) return []; // stale id (shouldn't happen — every mutator keeps order/maps in sync)
      const edit = editor.edits.get(slideIdx);
      if (!edit) return [base];
      return [{ ...base, title: edit.title, subtitle: edit.subtitle, main_points: [...edit.main_points] }];
    });
  }, [editor, baseSlidesById]);

  // Derived from the outline (+ local edits/additions/order) — see the module
  // header. `templatesById` is folded in for display where it has an entry,
  // so its absence costs previews, never rows.
  const choices = useMemo<SlideLayoutChoice[]>(() => {
    if (outlineState.status !== "ready") return [];
    return orderedSlides.map((slide) => {
      const suggestions = suggestionsFor(slide);
      const auto = suggestions[0]?.id ?? null;
      const override = overrides.get(slide.slide_idx);
      const templateId = override ?? auto;
      const template = templateId ? templatesById.get(templateId) ?? null : null;
      const ranked = templateId ? suggestions.find((s) => s.id === templateId) : undefined;
      return {
        slide,
        templateId,
        autoPicked: templateId !== null && override === undefined,
        layoutName: templateId ? template?.name ?? ranked?.name ?? nameFromTemplateId(templateId) : "",
        template,
        suggestions,
      };
    });
  }, [outlineState, orderedSlides, overrides, templatesById]);

  const unresolvedCount = choices.filter((choice) => choice.templateId === null).length;

  const choose = useCallback((slideIdx: number, templateId: string) => {
    setOverrides((prev) => {
      const next = new Map(prev);
      next.set(slideIdx, templateId);
      return next;
    });
  }, []);

  const autoSelectAll = useCallback(() => setOverrides(new Map()), []);

  const editSlide = useCallback((slideIdx: number, content: SlideContent) => {
    const sanitized = sanitizeContent(content);
    if (!sanitized) return; // a blank title is refused rather than saved
    setEditor((prev) => ({ ...prev, edits: new Map(prev.edits).set(slideIdx, sanitized) }));
  }, []);

  const addSlide = useCallback((afterSlideIdx: number | null) => {
    setEditor((prev) => {
      if (prev.order.length >= SLIDE_COUNT_MAX) return prev;
      const newIdx = prev.nextLocalIdx;
      const order = [...prev.order];
      order.splice(insertionIndex(prev.order, afterSlideIdx), 0, newIdx);
      return {
        ...prev,
        order,
        addedSlides: new Map(prev.addedSlides).set(newIdx, blankSlide(newIdx)),
        nextLocalIdx: newIdx - 1,
      };
    });
  }, []);

  const removeSlide = useCallback((slideIdx: number) => {
    setEditor((prev) => {
      if (prev.order.length <= 1) return prev; // a deck needs at least one slide
      const order = prev.order.filter((idx) => idx !== slideIdx);
      if (order.length === prev.order.length) return prev; // unknown id — no-op
      const addedSlides = prev.addedSlides.has(slideIdx)
        ? new Map([...prev.addedSlides].filter(([idx]) => idx !== slideIdx))
        : prev.addedSlides;
      const edits = prev.edits.has(slideIdx)
        ? new Map([...prev.edits].filter(([idx]) => idx !== slideIdx))
        : prev.edits;
      return { ...prev, order, addedSlides, edits };
    });
    setOverrides((prev) => {
      if (!prev.has(slideIdx)) return prev;
      const next = new Map(prev);
      next.delete(slideIdx);
      return next;
    });
  }, []);

  const moveSlide = useCallback((slideIdx: number, direction: "up" | "down") => {
    setEditor((prev) => {
      const at = prev.order.indexOf(slideIdx);
      const target = direction === "up" ? at - 1 : at + 1;
      if (at === -1 || target < 0 || target >= prev.order.length) return prev;
      const order = [...prev.order];
      [order[at], order[target]] = [order[target]!, order[at]!];
      return { ...prev, order };
    });
  }, []);

  const canAddSlide = editor.order.length < SLIDE_COUNT_MAX;

  const plan = useMemo<TemplatePlan | null>(() => {
    if (outlineState.status !== "ready" || choices.length === 0 || unresolvedCount > 0) return null;
    const slides: TemplatePlanSlide[] = choices.map((choice) => ({
      slide_idx: choice.slide.slide_idx,
      title: choice.slide.title,
      subtitle: choice.slide.subtitle ?? null,
      intent_tag: choice.slide.intent_tag,
      main_points: choice.slide.main_points ?? [],
      template_id: choice.templateId!,
      autoPicked: choice.autoPicked,
    }));
    return {
      requestId: outlineState.outline.request_id,
      title: outlineState.outline.title,
      slides,
    };
  }, [outlineState, choices, unresolvedCount]);

  return {
    outlineState,
    catalogState,
    templatesById,
    choices,
    unresolvedCount,
    loadOutline: loadOutlineTracked,
    regenerate,
    retryCatalog: requestLibrary,
    requestLibrary,
    choose,
    autoSelectAll,
    editSlide,
    addSlide,
    removeSlide,
    moveSlide,
    canAddSlide,
    plan,
  };
}
